import React, { useEffect, useMemo, useState } from "react";
import styles from "./PackagesModal.module.scss";
import { strings } from "intl/fa";
import { CloseIcon } from "@bale/ui";
import { createPackageInfoObject, packagesTitle } from "./packages";
import { formatStrings } from "utils/utils";
import { digitsToHindi } from "@bale/utils";
import { useRequest } from "hooks/useRequest";
import { getEpochDistanceInDays } from "utils/time";
import {
  getAvailableCampaignStartDateThunk,
  GetDefaultCampaignPackagesThunk,
} from "core/orderAd/orderAd.thunk";
import FullpageModal from "components/commons/modal/fullpageModal/FullpageModal";
import { PackageItem } from "./PackageItem";
import { CampaignPackageType } from "core/orderAd/orderAd.entity";
import { IconComponent } from "components/commons/iconComponent/IconComponent";
import PlayerModal from "components/commons/modal/playerModal/PlayerModal";
import { links as TutorialLinks } from "../tutorials/tutorials";
import { useAppSelector } from "utils/store";
import { campaignDefaultPackagesSelector } from "core/orderAd/orderAd.selector";
import { toast } from "react-toastify";
import LoadingSpinner from "components/commons/loading/LoadingSpinner";
import { ThreeUser } from "@bale/icons";

export const PackageIcons = {
  basic: <IconComponent img="/icons/giftCard.svg" size={32} />,
  advanced: <IconComponent img="/icons/club.svg" size={32} />,
  business: <ThreeUser size={32} />,
  custom: <IconComponent img="/icons/authentication.svg" size={32} />,
};

type Props = {
  onClose: () => void;
};

enum Tabs {
  Basic,
  Advanced,
  Business,
}

const TabsInfo = [
  {
    value: Tabs.Basic,
    htmlId: "#basic",
    title: packagesTitle[CampaignPackageType.BASIC],
  },
  {
    value: Tabs.Advanced,
    htmlId: "#advanced",
    title: packagesTitle[CampaignPackageType.ADVANCED],
  },
  {
    value: Tabs.Business,
    htmlId: "#business",
    title: packagesTitle[CampaignPackageType.BUSINESS],
  },
];

const scrollHandler = (packagesList: HTMLElement, sections: HTMLElement[]) => {
  const scrollPos = packagesList.scrollTop || packagesList.scrollTop;
  for (let section in sections) {
    const sectionElement = sections[section] as HTMLElement;
    if (
      sections.hasOwnProperty(section) &&
      sectionElement.offsetTop <= scrollPos + sectionElement.clientHeight
    ) {
      document
        .querySelectorAll(`a:not([href*=${sectionElement.id}])`)
        .forEach((el) => {
          el?.classList.remove(styles.Active);
        });
      document
        .querySelector(`a[href*=${sectionElement.id}]`)
        ?.classList?.add(styles.Active);
    }
  }
};

function PackagesModal(props: Props) {
  const [showPlayerModal, setShowPlayerModal] = useState<
    undefined | TutorialLinks
  >();
  const [packageStartDate, setPackageStartDate] = useState<string>();

  const { sendRequest: getMinCampaignStartDate } = useRequest(
    getAvailableCampaignStartDateThunk
  );
  const campaignDefaultPackages = useAppSelector(
    campaignDefaultPackagesSelector
  );
  const { sendRequest: getDefaultCampaignPackages } = useRequest(
    GetDefaultCampaignPackagesThunk
  );

  useEffect(() => {
    if (!campaignDefaultPackages) {
      getDefaultCampaignPackages();
      return;
    }
    document.querySelector("#advanced")!.scrollIntoView({
      behavior: "smooth",
    });
    const sections = [
      document.getElementById("basic")!,
      document.getElementById("advanced")!,
      document.getElementById("business")!,
    ];
    const packagesList = document.getElementById("package-scroll")!;
    const handleScroll = () => scrollHandler(packagesList, sections);
    packagesList.addEventListener("scroll", handleScroll);

    return () => {
      packagesList.removeEventListener("scroll", handleScroll);
    };
  }, [campaignDefaultPackages]);

  useEffect(() => {
    getMinCampaignStartDate({
      onSuccess: (res) => setPackageStartDate((res ?? Date.now()).toString()),
    });
  }, []);

  if (!campaignDefaultPackages) {
    return <></>;
  }

  return (
    <FullpageModal>
      <div className={styles.Fixed}>
        <div className={styles.CloseIcon}>
          <CloseIcon size={32} onClick={props.onClose} />
        </div>
        <div className={styles.Header}>
          <p className={styles.Title}>{strings.packages.modal.title}</p>
          <p className={styles.Description}>
            {strings.packages.modal.description1}
            <span className={styles.Emphasis}>
              {formatStrings(strings.packages.modal.description2, {
                days: digitsToHindi(
                  String(getEpochDistanceInDays(packageStartDate))
                ),
              })}
            </span>
            {strings.packages.modal.description3}
          </p>
        </div>
        <div className={styles.IOSTabs}>
          {TabsInfo.map((tab) => (
            <a
              key={tab.htmlId}
              href={tab.htmlId}
              className={styles.Tab}
              onClick={(event) => {
                event.currentTarget.classList.add(styles.Active);
              }}
            >
              {tab.title}
            </a>
          ))}
        </div>
      </div>
      <div className={styles.PackagesContainer}>
        <PackageItem
          package={createPackageInfoObject(
            campaignDefaultPackages.basic,
            CampaignPackageType.BASIC
          )}
          closeModal={props.onClose}
        />
        <PackageItem
          isRecommended
          package={createPackageInfoObject(
            campaignDefaultPackages.advanced,
            CampaignPackageType.ADVANCED
          )}
          closeModal={props.onClose}
        />
        <PackageItem
          package={createPackageInfoObject(
            campaignDefaultPackages.business,
            CampaignPackageType.BUSINESS
          )}
          closeModal={props.onClose}
        />
      </div>
      {showPlayerModal && (
        <PlayerModal
          aparatLink={showPlayerModal}
          onClose={() => setShowPlayerModal(undefined)}
        />
      )}
    </FullpageModal>
  );
}

export default PackagesModal;
